// Party long rest advances browser time, then saves each held completion atomically.
// No optimistic partial completion and no legacy rule request on any outcome.
import { resolveLongRest, skippedLongRest, longRestListeners, LONG_REST_IDS, type LongRestPlan } from '../janus-engine/turns/long-rest.js';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { buildAppliedFrame } from '../janus-engine/transport/applied-frame';
import { resolveLongRestTriggers, longRestTriggerSkipFrame } from '../janus-engine/turns/long-rest-triggers.js';
import { abilityTruthReaders, commitZoneEntryVerdict, hydrateReferencedDefinitions } from './ability-truth.js';
import { certifyMembers } from './environmental-payload-truth.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { ecId, ecLean, itemField } from '../janus-engine/ability/context.js';
import { RESOURCE_SCHEMA, applyResourceValues, type ResourceValues } from '../janus-engine/resources/resource-adjust.js';
import { setAbilityCharges, setInventoryCharges } from '../janus-engine/charges/charge-adjust.js';
import { removeStatusFromRow, statusRows, statusItemId } from '../janus-engine/statuses/expiry.js';
import { timedActivityIsDue } from '../janus-engine/ability/timed-finish.js';
import { upsertStatusOnRow, tryCommitTimePush, retryUnresolvedTimePush, hasUnresolvedTimePush } from './time-truth.js';
import { readers, definitionOf } from './charge-truth.js';
import { actorRowFor, actorEncounterFor, actorOutbox, bankConfirmedRev } from './movement-truth.js';
import { defaultActorRowLoaderDeps, ensureActorRowHeld, postRowOnly } from '../shared/actor-row-loader.js';
import { ftClockCalendarFromArr, ftClockColsFromArr, ftCampaignTimeAddSeconds, type CampaignClockCols } from '../shared/campaign-clock.js';
import { handleCasterUpdate } from './caster-update.js';
import { abilityEntryId } from '../janus-engine/charges/charge-adjust.js';
import { ftC1BuildLongRestDerived, ftC1ForgetCarrierCatalogs } from './c1-possessor-shadow.js';
import { skipUnported, refuseUnported } from '../janus-engine/unported.js';
import type { CasterArr } from '../shared/array-types.js';
import { applyStatusRootFields, type StatusRootFields } from '../janus-engine/compile/status-root-residue.js';
import { sharedDefinitionStore } from '../janus-engine/store/definition-store.js';
import { heldLayerlessDefinition } from '../janus-engine/compile/held-definition.js';
import { inventoryDecisionBefore, inventoryDecisionView } from '../janus-engine/inventory/inventory-decision-view.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const ENDPOINT = '/options/game/actions/resource_truth_commit_inc.php';
const warn = (message: string): void => { if (typeof displayWarning === 'function') displayWarning(message); };
export interface LongRestCommand extends Omit<LongRestPlan, 'completed'> {
  schema: typeof RESOURCE_SCHEMA; kind: 'long_rest'; operation_id: string; encounter_id: string; actor_id: string;
  expected_rev: number; roll_private: number; delta: 0;
  derived?: Row;
}
interface LongRestFrame { values: ResourceValues; statuses: LongRestPlan['statuses']; charges: LongRestPlan['charges']; derived?: Row }
export interface LongRestWire {
  schema: typeof RESOURCE_SCHEMA; kind: 'long_rest'; operation_id: string; encounter_id: string; actor_id: string;
  expected_rev?: number; rev: number; frame: LongRestFrame; committed?: boolean; correction?: boolean; provisional?: boolean;
  local?: boolean; pushed?: boolean;
}
const received = new Set<string>();
const locallyApplied = new Set<string>();
const unknown = new Map<string, LongRestCommand>();
const saving = new Set<string>();
// A saved compile skip is not a completion, including an unknown-answer retry
// or a matching WS acknowledgment. This disposition stays local to its operation.
const incompleteRestOperations = new Set<string>();
type RestTriggerWork = { actorId: string; encounterId: string; operationId: string; clock: CampaignClockCols;
  calendar: NonNullable<ReturnType<typeof ftClockCalendarFromArr>>; rollPrivate: number };
const triggerWork = new Map<string, RestTriggerWork>();
const triggerSaves = new Map<string, Promise<void>>();

/** Only the initiating browser owns this hook; a WS replay applies rows without re-firing. */
async function fireRestTriggers(work: RestTriggerWork): Promise<void> {
  const prior = triggerSaves.get(work.operationId);
  if (prior) return prior;
  const task = (async (): Promise<void> => {
    let held = await ensureActorRowHeld(work.actorId, work.encounterId);
    if (held.kind !== 'encounter') throw new Error('long-rest:trigger-actor-not-held');
    let encounter = actorEncounterFor(held.encounterId) as unknown as Row | null;
    if (!encounter) throw new Error('long-rest:trigger-encounter-not-held');
    const listeners = longRestListeners(held.row, { ...readers, definition: definitionOf });
    if (!listeners.length) return;
    const definitions = listeners.flatMap(l => l.definition ? [l.definition] : []);
    await hydrateReferencedDefinitions(held.row, encounter, definitions).catch(() => {});
    const certified = await certifyMembers(held.encounterId);
    held = await ensureActorRowHeld(work.actorId, work.encounterId);
    if (held.kind !== 'encounter') throw new Error('long-rest:trigger-actor-not-held');
    encounter = actorEncounterFor(held.encounterId) as unknown as Row | null;
    if (!encounter) throw new Error('long-rest:trigger-encounter-not-held');
    const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
    const members = certified?.members ?? new Map(roster.map(row => [ecId(row), row])); members.set(work.actorId, held.row);
    const read = abilityTruthReaders(held.row, encounter, certified?.complete ?? new Set());
    const verdict = certified ? resolveLongRestTriggers({ actorId: work.actorId, rollPrivate: work.rollPrivate }, encounter, members, {
      ...read, campaignClock: () => work.clock, addCampaignSeconds: (clock, seconds) => ftCampaignTimeAddSeconds(clock, work.calendar, seconds),
    }, browserRollSource()) : longRestTriggerSkipFrame(listeners.map(l => l.name), { actorId: work.actorId, reason: 'participant-compile-not-held' });
    if (!verdict.rows.length) return; // no due listener, or the carrier's ordinary gates vetoed it
    const before = inventoryDecisionBefore(members, verdict.ec);
    const expectedRevs = new Map([...members].map(([id, row]) => [id, Number(ecLean(row, 'movement_rev'))]));
    const abilityId = upper(itemField(definitions[0] ?? null, 'item_id')) || work.actorId;
    const saveSkip = async (reason: string): Promise<void> => {
      const skipped = longRestTriggerSkipFrame(listeners.map(l => l.name), { actorId: work.actorId, reason });
      const failed = (why: string): void => warn('Long-rest trigger skip could not be saved: '
        + escapeHtml(listeners.map(l => l.name).join(', ')) + ' (' + escapeHtml(why) + ')');
      const saved = await commitZoneEntryVerdict({ encounterId: work.encounterId, actorId: work.actorId, abilityId, zoneStatusId: '', verdict: skipped,
        longRest: { operationId: work.operationId + ':skip', onLateFailure: failed } });
      if (saved.outcome !== 'served' && !saved.parked) failed(saved.reason ?? saved.outcome);
    };
    const result = await commitZoneEntryVerdict({ encounterId: work.encounterId, actorId: work.actorId, abilityId, zoneStatusId: '', verdict,
      before, expectedRevs, longRest: { operationId: work.operationId, onLateFailure: reason => { void saveSkip(reason).catch(error => warn(String(error))); } } });
    // A parked command still owns its exact frame in the outbox; a later success owes no skip.
    if (result.outcome === 'served' || result.parked) return;
    await saveSkip(result.reason ?? result.outcome);
  })();
  triggerSaves.set(work.operationId, task);
  void task.finally(() => { if (triggerSaves.size > 256) triggerSaves.delete(triggerSaves.keys().next().value!); }).catch(() => {});
  return task;
}
async function afterRestSaved(command: LongRestCommand): Promise<void> {
  const work = triggerWork.get(command.operation_id);
  if (!work || partyCompletion) return;
  await fireRestTriggers(work).catch(error => warn(String(error)));
  triggerWork.delete(command.operation_id);
}
function clearUnknown(actorId: string, operationId: string): void {
  if (upper(unknown.get(upper(actorId))?.operation_id) === upper(operationId)) unknown.delete(upper(actorId));
}
function rowsHeld(): Row[] {
  const ft = window.FateTide as unknown as { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> } | undefined;
  const rows: Row[] = [];
  for (const value of ft?.casterCache?.values() ?? []) { const row = asRow(value); if (row) rows.push(row); }
  for (const value of ft?.encounterCache?.values() ?? []) for (const row of Array.isArray(asRow(value)?.encounter_creatures_arr) ? asRow(value)!.encounter_creatures_arr as Row[] : []) if (!rows.includes(row)) rows.push(row);
  return rows;
}
/** Apply the whole plan once; an optimistic socket echo does not confirm persistence. */
export function handleLongRestTruth(command: LongRestWire): void {
  if (command?.schema !== RESOURCE_SCHEMA || command.kind !== 'long_rest' || command.provisional || command.correction || !command.committed
    || !Number.isInteger(command.rev)) return;
  const actorId = upper(command.actor_id), frame = command.frame;
  if (!frame || !asRow(frame.values) || !asRow(frame.statuses) || !Array.isArray(frame.charges)) return;
  // Our optimistic socket echo proves delivery, not persistence. Keep an unknown
  // save retryable until HTTP (or the old server-committed frame) confirms it.
  if (command.pushed && locallyApplied.has(command.operation_id)) return;
  if (!command.local) {
    clearUnknown(actorId, command.operation_id);
    if (received.has(command.operation_id)) return;
    received.add(command.operation_id); if (received.size > 256) received.delete(received.values().next().value!);
    if (locallyApplied.has(command.operation_id)) return;
  } else {
    if (locallyApplied.has(command.operation_id)) return;
    locallyApplied.add(command.operation_id); if (locallyApplied.size > 256) locallyApplied.delete(locallyApplied.values().next().value!);
  }
  const actor = actorRowFor(command.encounter_id, actorId);
  // An older rest frame must never overwrite a newer actor mutation.
  if (actor && Number(actor.encounter_creatures.movement_rev ?? 0) > command.rev) return;
  for (const row of rowsHeld()) {
    const own = upper(asRow(row.encounter_creatures)?.encounter_creature_id) === actorId;
    for (const removed of frame.statuses.remove ?? []) removeStatusFromRow(row, removed.status_id);
    for (const change of frame.statuses.stack ?? []) {
      const statuses = asRow(row.statuses);
      for (const key of ['target', 'caster']) for (const s of Array.isArray(statuses?.[key]) ? statuses![key] as Row[] : []) if (upper(s.status_id) === upper(change.status_id)) s.status_stack = change.to;
    }
    for (const added of frame.statuses.add ?? []) {
      const me = upper(asRow(row.encounter_creatures)?.encounter_creature_id);
      if (upper(added.status_target_id) === me) {
        const shell = asRow(added.status_arr);
        const definition = shell && heldLayerlessDefinition(shell)
          && (shell.item_layer_types === undefined || Array.isArray(shell.item_layer_types) && shell.item_layer_types.length === 0)
          ? { ...shell, item_layer_types: {} } : shell;
        // Later saved-row updates remove inline status_arr. Keep the exact
        // delivered definition where the page's status readers resolve lean refs.
        const id = upper(itemField(definition, 'item_id'));
        if (definition && id === upper(added.status_item_id) && heldLayerlessDefinition(definition)) {
          const catalog = (row.catalog ??= {}) as Row;
          if (!heldLayerlessDefinition(asRow(catalog[id]))) catalog[id] = definition;
        }
        upsertStatusOnRow(row, definition !== shell ? { ...added, status_arr: definition } : added);
      }
    }
    if (!own) continue;
    if (frame.derived) {
      const computed = (row.computed ??= {}) as Row;
      for (const key of ['creature', 'vitality']) if (asRow(frame.derived['computed.' + key])) computed[key] = structuredClone(frame.derived['computed.' + key]);
      for (const [key, value] of Object.entries(asRow(frame.derived.root_buckets) ?? {})) {
        ((row.item_layer_types ??= {}) as Row)[key] = structuredClone(value);
        delete row[key];
      }
      for (const [key, value] of Object.entries(asRow(frame.derived.root_scalars) ?? {})) { if (value === null) delete row[key]; else row[key] = value; }
      applyStatusRootFields(row, asRow(frame.derived.root_fields) as StatusRootFields | undefined);
    }
    for (const pool of frame.charges) {
      if (pool.kind === 'inventory') setInventoryCharges(row, pool.holder, pool.to, readers);
      else setAbilityCharges(row, pool.item_id, pool.to, readers);
    }
    applyResourceValues(row, frame.values, { layer: (r, k) => readers.value(r, k), hasLayer: (r, k) => readers.value(r, k) != null });
    const ec = (row.encounter_creatures ??= {}) as Row; ec.movement_rev = command.rev;
    handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
  }
  if (actor) bankConfirmedRev(command.encounter_id, actorId, actor, command.rev);
  ftC1ForgetCarrierCatalogs([actorId]);
  window.dispatchEvent(new CustomEvent('ft:long-rest-applied', { detail: { command } }));
}
/** Transport retries retain exactly the same operation and raw plan after an unknown answer. */
async function save(command: LongRestCommand): Promise<'handled' | 'refused' | 'unresolved'> {
  const prior = unknown.get(command.actor_id);
  command = prior?.operation_id === command.operation_id ? prior : structuredClone(command);
  unknown.set(command.actor_id, command);
  if (window.ftPushAppliedTruth) {
    const wire = { schema: command.schema, kind: command.kind, operation_id: command.operation_id, actor_id: command.actor_id,
      encounter_id: command.encounter_id, rev: command.expected_rev + 1,
      frame: { values: command.after, statuses: command.statuses, charges: command.charges, ...(command.derived ? { derived: command.derived } : {}) }, committed: true };
    handleLongRestTruth({ ...wire, local: true });
    window.ftPushAppliedTruth({ type: 'janus_resource_truth', command: wire,
      applied_frame: buildAppliedFrame({ operation_id: command.operation_id, encounter_id: command.encounter_id, actor_id: command.actor_id,
        rev: wire.rev, member_revs: {}, rows: [], computed: {}, new_entities: [], deletes: [] }) });
  }
  const { status, body } = await postRowOnly(ENDPOINT, command as unknown as Row, {
    ...defaultActorRowLoaderDeps(), fetchImpl: (url, init) => saveAppliedTruth(url, command, init),
  });
  if (status >= 200 && status < 300 && body?.ok === true && Number.isInteger(body.rev) && asRow(body.frame)) {
    clearUnknown(command.actor_id, command.operation_id);
    handleLongRestTruth({ ...command, rev: body.rev as number, frame: body.frame as unknown as LongRestFrame, committed: true });
    await afterRestSaved(command);
    return incompleteRestOperations.has(command.operation_id) ? 'refused' : 'handled';
  }
  if (received.has(command.operation_id)) { clearUnknown(command.actor_id, command.operation_id); await afterRestSaved(command); return incompleteRestOperations.has(command.operation_id) ? 'refused' : 'handled'; } // WS already confirmed this exact save
  if (status >= 400 && status < 500) {
    clearUnknown(command.actor_id, command.operation_id);
    triggerWork.delete(command.operation_id);
    if (status === 409 && /operation (id reused|receipt missing)/i.test(String(body?.error ?? '')))
      refuseUnported('long-rest:receipt-conflict', { actorId: command.actor_id, operationId: command.operation_id, error: body?.error });
    else warn('Long-rest completion was refused: ' + String(body?.error ?? status));
    return 'refused';
  }
  warn('Long-rest completion could not be confirmed. Retry the saved completion before taking another rest.');
  return 'unresolved';
}
export async function tryCommitLongRest(input: { actorId: string; encounterId: string; clock: CampaignClockCols; calendar: NonNullable<ReturnType<typeof ftClockCalendarFromArr>>; playerControlledSummon?: boolean; rollPrivate?: number; operationId?: string }): Promise<'handled' | 'refused' | 'unresolved'> {
  const actorId = upper(input.actorId);
  if (saving.has(actorId)) return 'unresolved';
  saving.add(actorId);
  try {
    const prior = unknown.get(actorId); if (prior) return await save(prior);
    // One readiness budget for this actor, including definition adoption and both
    // compile attempts. Only these read-only tasks may outlive it; never race a save.
    const deadline = Date.now() + 35_000;
    const ready = async <T>(task: Promise<T>): Promise<T | null> => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        return await Promise.race([task, new Promise<null>(resolve => {
          timer = setTimeout(() => resolve(null), Math.max(0, deadline - Date.now()));
        })]);
      } finally { clearTimeout(timer); }
    };
    let held = await ensureActorRowHeld(actorId, input.encounterId);
    if (held.kind !== 'encounter') { warn(held.kind === 'refused' ? held.message : 'Long rest needs an encounter.'); return 'refused'; }
    const store = sharedDefinitionStore(BASE_URL);
    const definition = (id: string): Row | null => definitionOf(id) ?? store.definition(id);
    const missing = [LONG_REST_IDS.fullyRested, LONG_REST_IDS.awake].filter(id => !definition(id));
    const definitionsReady = !missing.length || await ready(store.getDefinitions(missing).then(() => true).catch(() => true));
    // Re-resolve after loading: never decide against a row read before a wait.
    held = await ensureActorRowHeld(actorId, held.encounterId);
    if (held.kind !== 'encounter') return 'refused';
    // The raw SELECT must include earlier saved luck/resources and their movement revision.
    const outbox = actorOutbox(held.encounterId, actorId);
    const saves = outbox.pending({ includeAppliedSaves: false }) ? null : outbox.settleAppliedSaves();
    if (saves) {
      if (!await saves && !outbox.blocked()) { warn('Long rest could not complete because an earlier actor save was refused.'); return 'unresolved'; }
      held = await ensureActorRowHeld(actorId, held.encounterId);
      if (held.kind !== 'encounter') return 'refused';
    }
    const readDeps = { ...defaultActorRowLoaderDeps(), maxAttempts: 1 as const };
    const readSnapshot = (row: Row, encounterId: string) => postRowOnly(ENDPOINT, {
      schema: RESOURCE_SCHEMA, kind: 'long_rest_read', operation_id: crypto.randomUUID().toUpperCase(),
      actor_id: actorId, encounter_id: encounterId, expected_rev: Number(ecLean(row, 'movement_rev')), roll_private: input.rollPrivate ?? 0,
    }, readDeps);
    const readable = (answer: Awaited<ReturnType<typeof postRowOnly>>): boolean => answer.status === 200 && answer.body?.ok === true
      && !!asRow(answer.body.ec) && Array.isArray(answer.body.statuses) && Array.isArray(answer.body.charges);
    let snapshot = await readSnapshot(held.row, held.encounterId);
    if (!readable(snapshot) && (snapshot.status === 0 || snapshot.status === 409 || snapshot.status >= 500 || snapshot.status === 200)) {
      // This is a SELECT: refresh once before retrying, without replaying a rest or its dice.
      if (readDeps.resyncEncounter) {
        try {
          await readDeps.resyncEncounter(held.encounterId);
          const fresh = await ensureActorRowHeld(actorId, held.encounterId);
          if (fresh.kind !== 'encounter') { warn(fresh.kind === 'refused' ? fresh.message : 'Long rest needs an encounter.'); return 'refused'; }
          held = fresh;
          snapshot = await readSnapshot(held.row, held.encounterId);
        } catch { /* Keep the failed answer's status and words below if the fresh row is unavailable. */ }
      }
    }
    const rawEc = asRow(snapshot.body?.ec);
    if (!readable(snapshot) || !rawEc) {
      warn('The saved long-rest state could not be read (HTTP ' + snapshot.status + '): '
        + escapeHtml(snapshot.body?.error ?? (snapshot.body ? 'incomplete saved-state answer' : 'no JSON answer'))); return 'refused';
    }
    held = await ensureActorRowHeld(actorId, held.encounterId);
    if (held.kind !== 'encounter') return 'refused';
    // The SELECT supplies exact raw NULL/zero/CAS state; rules still read the browser's compile.
    const compiledStatuses = asRow(held.row.statuses);
    const target = statusRows(held.row, 'target');
    const decisionRow: Row = { ...held.row, encounter_creatures: { ...asRow(held.row.encounter_creatures), ...rawEc },
      statuses: { ...compiledStatuses, target: (snapshot.body!.statuses as Row[]).map(s => ({ ...target.find(t => upper(t.status_id) === upper(s.status_id)), ...s })) },
      charges: snapshot.body!.charges, raw_charge_snapshot: true, raw_status_snapshot: true };
    const plan = !definitionsReady ? skippedLongRest('status-compile-unported:client-timeout', actorId)
      : outbox.blocked() || outbox.pending({ includeAppliedSaves: false }) > 0 ? skippedLongRest('actor-save-pending', actorId)
      : Number(asRow(held.row.encounter_creatures)?.movement_rev ?? 0) !== Number(rawEc.movement_rev ?? 0) ? skippedLongRest('compiled-revision-unavailable', actorId)
      : resolveLongRest({ row: decisionRow, clock: input.clock, calendar: input.calendar, playerControlledSummon: input.playerControlledSummon === true }, {
        ...readers, definition,
        statusBase: id => {
          const info = (window.FateTide as unknown as { statusStackInfo?: Record<string, { base?: string; level_index?: number; quantity?: boolean }> }).statusStackInfo?.[upper(id)];
          return info?.quantity && info.base ? { base: info.base, count: info.level_index ?? null } : null;
        },
      }, () => crypto.randomUUID().toUpperCase());
    const operationId = plan.completed && upper(input.operationId) || crypto.randomUUID().toUpperCase();
    const nameTimeout = (row: LongRestPlan['rows'][number]): void => {
      row.roll_to = actorId;
      row.rolls_full += ' (' + String(ecLean(held.row, 'encounter_creature_name') || itemField(held.row, 'item_name') || 'Party member') + ')';
    };
    if (!definitionsReady) nameTimeout(plan.rows[0]!);
    let derived: Row | null = null;
    if (plan.completed && (plan.statuses.add.length || plan.statuses.remove.length || plan.statuses.stack.length)) {
      const snapshotRow = inventoryDecisionView(new Map([[actorId, decisionRow]])).rest(actorId, plan.after as ResourceValues, plan.charges, readers);
      // A held LAYERLESS status (a Monk stance, Crafting Progress) is never a full page copy, so the build met its definition only in a
      // def-missing retry round, which a busy page's 6 s budget had already spent (fix-bug fblongrest). Fetch the carrier's held statuses
      // up front, best effort: one the viewer cannot read still refuses by name if the compile needs it.
      const heldStatusIds = [...new Set(['target', 'caster'].flatMap(mode => {
        const list = asRow(snapshotRow.statuses)?.[mode];
        return (Array.isArray(list) ? list : Object.values(asRow(list) ?? {})).map(status => upper(asRow(status)?.status_item_id));
      }))].filter(Boolean);
      const statusesReady = !heldStatusIds.length || await ready(store.getDefinitions(heldStatusIds).then(() => true).catch(() => true));
      const build = () => ftC1BuildLongRestDerived({ targetId: actorId, encounterId: held.encounterId, operationId, rowSnapshot: snapshotRow,
        remove: plan.statuses.remove.map(s => ({ status_id: s.status_id, item_id: s.before.status_item_id })),
        stack: plan.statuses.stack.map(s => ({ status_id: s.status_id, item_id: upper(target.find(t => upper(t.status_id) === s.status_id)?.status_item_id), delta: s.to - s.from })), add: plan.statuses.add })
        .catch((error: unknown) => ({ reason: `compile-error:${error instanceof Error ? error.message : String(error)}` }));
      const timeout: { reason: string; detail?: Row } = { reason: 'client-timeout' };
      let compiled: Awaited<ReturnType<typeof ftC1BuildLongRestDerived>> = statusesReady && Date.now() < deadline
        ? await ready(build()) ?? timeout : timeout;
      // A build that ran out of fetch budget retries once against the now-warm mirror and store (move-handlers' c1RefusalReason rule);
      // a definition the server already refused stays refused.
      const transient = (reason: string): boolean => /^(client-timeout|carrier-catalog-missing)/.test(reason)
        || reason.startsWith('def-missing:') && reason.slice(12).split(',').every(id => sharedDefinitionStore(BASE_URL).refusal(id) === null);
      if (!('derived' in compiled) && transient(compiled.reason) && Date.now() < deadline) compiled = await ready(build()) ?? timeout;
      if ('derived' in compiled) derived = compiled.derived;
      else {
        incompleteRestOperations.add(operationId);
        if (incompleteRestOperations.size > 256) incompleteRestOperations.delete(incompleteRestOperations.values().next().value!);
        plan.completed = false;
        plan.statuses = { remove: [], stack: [], add: [] };
        plan.rows.push(skipUnported(`long-rest:status-compile-unported:${compiled.reason}`, {
          ...(asRow('detail' in compiled ? compiled.detail : null) ?? {}), actorId, encounterId: held.encounterId,
        }));
        if (compiled.reason === 'client-timeout') nameTimeout(plan.rows[plan.rows.length - 1]!);
      }
    }
    const { completed: _completed, ...writes } = plan; void _completed;
    if (plan.completed) triggerWork.set(operationId, { actorId, encounterId: held.encounterId, operationId,
      clock: input.clock, calendar: input.calendar, rollPrivate: input.rollPrivate ?? 0 });
    const saved = await save({ ...writes, ...(derived ? { derived } : {}), schema: RESOURCE_SCHEMA, kind: 'long_rest', operation_id: operationId,
      actor_id: actorId, encounter_id: held.encounterId, expected_rev: Number(rawEc.movement_rev ?? 0), roll_private: input.rollPrivate ?? 0, delta: 0 });
    return saved === 'handled' && incompleteRestOperations.has(operationId) ? 'refused' : saved;
  } finally { saving.delete(actorId); }
}
let partyBusy = false;
let partyCompletion: { source: 'party' | 'expiry'; players: Array<{ actorId: string; encounterId: string; summon: boolean; operationId?: string }>; clock: CampaignClockCols;
  calendar: NonNullable<ReturnType<typeof ftClockCalendarFromArr>>; rollPrivate: number; campaignId: string; boundaryConfirmed: boolean } | null = null;
async function finishPartyCompletion(): Promise<'handled' | 'refused' | 'unresolved'> {
  const completion = partyCompletion;
  if (!completion) return 'refused';
  let result: 'handled' | 'refused' = 'handled';
  while (completion.players.length) {
    const player = completion.players[0]!;
    const saved = await tryCommitLongRest({ actorId: player.actorId, encounterId: player.encounterId, playerControlledSummon: player.summon,
      clock: completion.clock, calendar: completion.calendar, rollPrivate: completion.rollPrivate, operationId: player.operationId });
    if (saved === 'unresolved') return 'unresolved'; // retain THIS actor and all later actors; no second time advance
    completion.players.shift();
    if (saved === 'refused') result = 'refused';
  }
  // Finish the whole boundary first: a later ally's reset must never erase an earlier carrier's effect.
  for (const work of triggerWork.values()) {
    await fireRestTriggers(work).catch(error => warn(String(error)));
    triggerWork.delete(work.operationId);
  }
  partyCompletion = null;
  return result;
}
export async function tryCommitPartyLongRest(input: { campaignId: string; encounterId: string; rollPrivate?: number }): Promise<'handled' | 'refused' | 'unresolved'> {
  if (partyBusy) { warn('The prior long rest is still awaiting a save answer.'); return 'unresolved'; }
  partyBusy = true;
  try {
    const campaignId = upper(input.campaignId), encounterId = upper(input.encounterId);
    if (partyCompletion) {
      if (partyCompletion.campaignId !== campaignId) { warn('Complete the prior campaign rest first.'); return 'unresolved'; }
      if (!partyCompletion.boundaryConfirmed) {
        const recovered = await retryUnresolvedTimePush(campaignId);
        if (recovered !== 'handled') {
          if (recovered === null) partyCompletion = null; // a terminal refusal proves this boundary did not land
          return recovered === 'unresolved' ? 'unresolved' : 'refused';
        }
        partyCompletion.boundaryConfirmed = true;
      }
      return await finishPartyCompletion();
    }
    // An earlier unrelated time push must recover before a fresh party boundary is decided.
    if (hasUnresolvedTimePush(campaignId)) {
      const recovered = await retryUnresolvedTimePush(campaignId);
      return recovered === 'handled' ? 'handled' : recovered === 'unresolved' ? 'unresolved' : 'refused';
    }
    const campaign = asRow(window.FateTide?.campaignCache?.get(campaignId));
    const calendar = ftClockCalendarFromArr(campaign);
    if (!campaign || !calendar) { warn('Load the campaign clock before taking a party long rest.'); return 'refused'; }
    const playerIds = [...new Set(readers.entries(campaign, 'campaign_players').map(abilityEntryId).filter(Boolean))];
    if (!playerIds.length) { warn('No campaign players to long rest.'); return 'refused'; }
    const heldPlayers: Array<{ actorId: string; encounterId: string; summon: boolean }> = [];
    for (const actorId of playerIds) {
      const loaded = await ensureActorRowHeld(actorId);
      if (loaded.kind !== 'encounter') { warn('A party member could not be loaded; the party rest was not started.'); return 'refused'; }
      const encounter = actorEncounterFor(loaded.encounterId) as unknown as Row | null;
      if (Number(scalar(asRow(encounter?.encounters)?.in_combat ?? encounter?.in_combat) ?? 0) === 1) { warn('End combat before taking a party long rest.'); return 'refused'; }
      heldPlayers.push({ actorId, encounterId: loaded.encounterId, summon: false });
      const roster = Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as Row[] : [];
      for (const member of roster) if (upper(asRow(member.encounter_creatures)?.controller_encounter_creature_id) === actorId) {
        const summonId = upper(asRow(member.encounter_creatures)?.encounter_creature_id);
        if (summonId && !heldPlayers.some(p => p.actorId === summonId)) heldPlayers.push({ actorId: summonId, encounterId: loaded.encounterId, summon: true });
      }
    }
    // A separately invoked unresolved completion is retried before any new boundary.
    if (unknown.size) {
      for (const command of [...unknown.values()]) { const result = await save(command); if (result !== 'handled') return result; }
      return 'handled';
    }
    const derivedClock = asRow(asRow((window as unknown as { ftCampaignClock?: { getDerived?: () => unknown } }).ftCampaignClock?.getDerived?.())?.cols);
    const start = derivedClock && ['year', 'month', 'day', 'hour', 'minute', 'second'].every(key => Number.isInteger(derivedClock[key]))
      ? derivedClock as unknown as CampaignClockCols : ftClockColsFromArr(campaign) as CampaignClockCols;
    partyCompletion = { source: 'party', players: heldPlayers, clock: ftCampaignTimeAddSeconds(start, calendar, 8 * 3600), calendar,
      rollPrivate: input.rollPrivate ?? 0, campaignId, boundaryConfirmed: false };
    const completion = partyCompletion;
    const outcome = await tryCommitTimePush({ campaignId, encounterId, rounds: 4800, gmLog: true, label: 'Long rest', rollPrivate: input.rollPrivate ?? 0,
      onCommittedClock: clock => { completion.clock = clock; } });
    if (outcome !== 'handled') { if (outcome === null) partyCompletion = null; return outcome === 'unresolved' ? 'unresolved' : 'refused'; }
    completion.boundaryConfirmed = true;
    return await finishPartyCompletion();
  } finally { partyBusy = false; }
}
/** The time sender owns due Resting completions. All actors due in that push rest before any of their listeners fire. */
export async function completeDueLongRests(input: { campaignId: string; encounterId: string; clock: CampaignClockCols; rollPrivate?: number; actorIds?: readonly string[] }): Promise<'handled' | 'refused' | 'unresolved'> {
  if (partyCompletion) return partyCompletion.source === 'party' ? 'handled' : finishPartyCompletion();
  const campaign = asRow(window.FateTide?.campaignCache?.get(upper(input.campaignId))), calendar = ftClockCalendarFromArr(campaign);
  const encounter = actorEncounterFor(input.encounterId) as unknown as Row | null;
  if (!calendar || !encounter) return 'refused';
  const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
  const playerIds = new Set(readers.entries(campaign, 'campaign_players').map(abilityEntryId));
  const actorIds = input.actorIds ? new Set(input.actorIds.map(upper)) : null;
  const players = roster.flatMap(row => {
    if (actorIds && !actorIds.has(ecId(row))) return [];
    const status = statusRows(row, 'target').find(s => statusItemId(s) === LONG_REST_IDS.resting && timedActivityIsDue(s, input.clock));
    return status ? [{ actorId: ecId(row), encounterId: upper(input.encounterId), summon: playerIds.has(upper(ecLean(row, 'controller_encounter_creature_id'))), operationId: upper(status.status_id) }] : [];
  });
  if (!players.length) return 'handled';
  partyCompletion = { source: 'expiry', players, clock: input.clock, calendar, rollPrivate: input.rollPrivate ?? 0,
    campaignId: upper(input.campaignId), boundaryConfirmed: true };
  return finishPartyCompletion();
}
Object.assign(window, { ftTryCommitLongRest: tryCommitLongRest, ftTryCommitPartyLongRest: tryCommitPartyLongRest, ftHandleLongRestTruth: handleLongRestTruth,
  ftCompleteDueLongRests: completeDueLongRests });
